import { useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { BackendClient, currentUserId } from '@/infrastructure/clients/BackendClient';
import { captureError } from '@/utils/sentry';
import LureoButton from '@/common/LureoButton';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { useAuthUser } from '@/hooks/useAuthUser';
import { AuthShell, AuthField, AuthCrossLink, SignedInRedirect } from './AuthShell';
import { useAuthStatus } from './useAuthStatus';
import { takeReturnPath } from '@/modules/auth/returnAfterLogin';
import { TextButton } from '@/components/ui/text-button';

// GoTrue sends one reset email per address a minute
const RESET_COOLDOWN_MS = 60_000;

type ResetState = 'idle' | 'sending' | 'sent';

export const Login = () => {
  const { status, show, clearError } = useAuthStatus();
  const [password, setPassword] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [signingIn, setSigningIn] = useState(false);
  const [resetState, setResetState] = useState<ResetState>('idle');
  const busy = useRef({ signIn: false, reset: false });
  const navigate = useNavigate();
  const loginFormRef = useRef<HTMLFormElement>(null);
  const services = useServices();
  const { user, loading } = useAuthUser();

  useEffect(() => {
    if (resetState !== 'sent') return;
    const timer = setTimeout(() => {
      busy.current.reset = false;
      setResetState('idle');
    }, RESET_COOLDOWN_MS);
    return () => clearTimeout(timer);
  }, [resetState]);

  // Not while this page signs in, which goes where it was asked to itself
  if (!loading && user && !signingIn) return <SignedInRedirect />;

  const editEmail = (value: string) => {
    clearError();
    setEmail(value);
  };
  const editPassword = (value: string) => {
    clearError();
    setPassword(value);
  };

  async function onLoginSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy.current.signIn) return;
    busy.current.signIn = true;
    setSigningIn(true);

    const response = await BackendClient.auth.signInWithPassword({
      email,
      password
    });

    if (response.error) {
      busy.current.signIn = false;
      setSigningIn(false);
      show(response.error.message);
      return;
    }
    const u = response.data.user;
    if (u) {
      services.analytics.identify(u.id);
      services.analytics.trackLureoAppLogIn({ login_method: 'email' });
    }
    // Stays signing in until the next page shows
    navigate(takeReturnPath(currentUserId()) ?? '/dashboard', { replace: true });
  }

  const handleLoginKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      loginFormRef.current?.requestSubmit();
    }
  };

  async function onForgotPassword(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    if (busy.current.reset) return;
    if (!email) {
      show('Please enter your email address first');
      return;
    }
    busy.current.reset = true;
    setResetState('sending');
    try {
      const redirectUrl = `${window.location.origin}/reset-password`;
      const { error } = await BackendClient.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl
      });
      if (error) {
        captureError(new Error(`Password reset request failed: ${error.message}`), {
          domain: 'auth',
          operation: 'resetPasswordForEmail'
        });
        busy.current.reset = false;
        setResetState('idle');
        show(error.message);
      } else {
        setResetState('sent');
        show('Password reset instructions have been sent to your email', 'success');
      }
    } catch (error) {
      captureError(error, { domain: 'auth', operation: 'resetPasswordForEmail' });
      busy.current.reset = false;
      setResetState('idle');
      show('An error occurred. Please try again.');
    }
  }

  return (
    <AuthShell
      title="Sign in"
      subtitle="Welcome back."
      status={status}
      oauthTermsNote
      onError={show}
      footer={<AuthCrossLink prompt="Don't have an account?" to="/signup" action="Sign up" />}>
      <form ref={loginFormRef} onSubmit={onLoginSubmit} className="flex flex-col gap-3.5">
        <AuthField
          id="login-email"
          label="Email"
          type="email"
          value={email}
          onChange={editEmail}
          autoComplete="email"
          disabled={signingIn}
        />
        <AuthField
          id="login-password"
          label="Password"
          type="password"
          value={password}
          onChange={editPassword}
          onKeyDown={handleLoginKeyDown}
          autoComplete="current-password"
          disabled={signingIn}
        />
        <TextButton tone="link" onClick={onForgotPassword} disabled={resetState !== 'idle'} className="self-start">
          {resetState === 'sending' ? 'Sending...' : resetState === 'sent' ? 'Sent. Check your inbox' : 'Forgot password?'}
        </TextButton>
      </form>
      <LureoButton
        variant="secondary"
        className="h-10 w-full rounded-lureo-radius-button-pill"
        disabled={signingIn}
        onClick={() => loginFormRef.current?.requestSubmit()}>
        {signingIn ? 'Signing in...' : 'Sign in'}
      </LureoButton>
    </AuthShell>
  );
};
